import {
  CONSTRAINT_TYPE_LABELS,
  MODULES,
  PERMISSION_LEVELS,
  SCOPE_TYPE_LABELS,
  TENANT_STATUS_LABELS,
  TENANT_TYPE_LABELS
} from "./chunk-U7OIS35G.mjs";

// src/api/client.ts
import axios from "axios";
var _client = null;
function initAdminApi(config) {
  _client = axios.create({
    baseURL: config.baseURL,
    headers: { "Content-Type": "application/json" }
  });
  if (config.getAccessToken) {
    const getToken = config.getAccessToken;
    _client.interceptors.request.use(async (reqConfig) => {
      const token = await getToken();
      if (token) {
        reqConfig.headers.Authorization = `Bearer ${token}`;
      }
      return reqConfig;
    });
  }
  return _client;
}
function getAdminApi() {
  if (!_client) {
    throw new Error(
      "[@damar/admin-hooks] API client not initialized. Call initAdminApi() at app startup."
    );
  }
  return _client;
}

// src/api/userService.ts
var BASE = "/api/v1/admin/users";
var userService = {
  listUsers: async (query) => {
    const { data } = await getAdminApi().get(BASE, { params: query });
    return data;
  },
  getUser: async (userId) => {
    const { data } = await getAdminApi().get(`${BASE}/${userId}`);
    return data;
  },
  createUser: async (request) => {
    const { data } = await getAdminApi().post(BASE, request);
    return data;
  },
  updateUser: async (userId, request) => {
    const { data } = await getAdminApi().put(`${BASE}/${userId}`, request);
    return data;
  },
  changeUserStatus: async (userId, status) => {
    const { data } = await getAdminApi().patch(`${BASE}/${userId}/status`, { status });
    return data;
  },
  deleteUser: async (userId) => {
    await getAdminApi().delete(`${BASE}/${userId}`);
  },
  getCurrentUser: async () => {
    const { data } = await getAdminApi().get(`${BASE}/current`);
    return data;
  },
  assignProfiles: async (userId, profileIds) => {
    await getAdminApi().post(`${BASE}/${userId}/profiles`, { profileIds });
  },
  getUserProfileIds: async (userId) => {
    const { data } = await getAdminApi().get(`${BASE}/${userId}/profiles`);
    if (Array.isArray(data) && data.length > 0 && typeof data[0] === "object") {
      return data.map((p) => p.id);
    }
    return data;
  }
};

// src/api/tenantService.ts
var BASE2 = "/api/v1/admin/tenants";
var tenantService = {
  listTenants: async () => {
    const { data } = await getAdminApi().get(BASE2);
    return data;
  },
  getTenant: async (tenantId) => {
    const { data } = await getAdminApi().get(`${BASE2}/${tenantId}`);
    return data;
  },
  createTenant: async (request) => {
    const { data } = await getAdminApi().post(BASE2, request);
    return data;
  },
  updateTenant: async (tenantId, request) => {
    const { data } = await getAdminApi().put(`${BASE2}/${tenantId}`, request);
    return data;
  },
  changeTenantStatus: async (tenantId, status) => {
    const { data } = await getAdminApi().patch(`${BASE2}/${tenantId}/status`, { status });
    return data;
  },
  changeTenantType: async (tenantId, type) => {
    const { data } = await getAdminApi().patch(`${BASE2}/${tenantId}/type`, { type });
    return data;
  },
  extendTrial: async (tenantId, days) => {
    const { data } = await getAdminApi().post(`${BASE2}/${tenantId}/extend-trial`, { days });
    return data;
  },
  deleteTenant: async (tenantId) => {
    await getAdminApi().delete(`${BASE2}/${tenantId}`);
  }
};

// src/api/permissionService.ts
var BASE3 = "/api/v1/admin/permissions";
var permissionService = {
  listPermissions: async (query) => {
    const { data } = await getAdminApi().get(BASE3, { params: query });
    return data;
  },
  getPermission: async (id) => {
    const { data } = await getAdminApi().get(`${BASE3}/${id}`);
    return data;
  },
  createPermission: async (request) => {
    const { data } = await getAdminApi().post(BASE3, request);
    return data;
  },
  updatePermission: async (id, request) => {
    const { data } = await getAdminApi().put(`${BASE3}/${id}`, request);
    return data;
  },
  deletePermission: async (id) => {
    await getAdminApi().delete(`${BASE3}/${id}`);
  },
  getUserPermissions: async (userId) => {
    const { data } = await getAdminApi().get(`${BASE3}/user/${userId}`);
    return data;
  },
  getModulePermissions: async (module) => {
    const { data } = await getAdminApi().get(`${BASE3}/module/${module}`);
    return data;
  },
  getActivePermissions: async () => {
    const { data } = await getAdminApi().get(BASE3, { params: { isActive: true } });
    return data;
  }
};

// src/api/profileService.ts
var BASE4 = "/api/v1/admin/profiles";
var profileService = {
  listProfiles: async (query) => {
    const { data } = await getAdminApi().get(BASE4, { params: query });
    return data;
  },
  getProfile: async (id) => {
    const { data } = await getAdminApi().get(`${BASE4}/${id}`);
    return data;
  },
  createProfile: async (request) => {
    const { data } = await getAdminApi().post(BASE4, request);
    return data;
  },
  updateProfile: async (id, request) => {
    const { data } = await getAdminApi().put(`${BASE4}/${id}`, request);
    return data;
  },
  deleteProfile: async (id) => {
    await getAdminApi().delete(`${BASE4}/${id}`);
  },
  addPermission: async (profileId, permissionId) => {
    await getAdminApi().post(`${BASE4}/${profileId}/permissions/${permissionId}`);
  },
  removePermission: async (profileId, permissionId) => {
    await getAdminApi().delete(`${BASE4}/${profileId}/permissions/${permissionId}`);
  }
};

// src/api/moduleService.ts
var BASE5 = "/api/v1/admin/modules";
var TENANT_BASE = "/api/v1/admin/tenant-modules";
var moduleService = {
  // Module Catalog
  listModules: async () => {
    const { data } = await getAdminApi().get(BASE5);
    return data;
  },
  getModule: async (id) => {
    const { data } = await getAdminApi().get(`${BASE5}/${id}`);
    return data;
  },
  createModule: async (request) => {
    const { data } = await getAdminApi().post(BASE5, request);
    return data;
  },
  updateModule: async (id, request) => {
    const { data } = await getAdminApi().put(`${BASE5}/${id}`, request);
    return data;
  },
  deleteModule: async (id) => {
    await getAdminApi().delete(`${BASE5}/${id}`);
  },
  // Tenant Modules
  listTenantModules: async (tenantId) => {
    const { data } = await getAdminApi().get(TENANT_BASE, { params: { tenantId } });
    return data;
  },
  activateModule: async (tenantId, moduleId) => {
    const { data } = await getAdminApi().post(TENANT_BASE, { tenantId, moduleId });
    return data;
  },
  deactivateModule: async (tenantId, moduleId) => {
    await getAdminApi().delete(`${TENANT_BASE}/${tenantId}/${moduleId}`);
  }
};

// src/api/licenceService.ts
var PLAN_BASE = "/api/v1/admin/licence-plans";
var TENANT_BASE2 = "/api/v1/admin/tenant-licences";
var licenceService = {
  // Licence Plans
  listPlans: async () => {
    const { data } = await getAdminApi().get(PLAN_BASE);
    return data;
  },
  getPlan: async (id) => {
    const { data } = await getAdminApi().get(`${PLAN_BASE}/${id}`);
    return data;
  },
  createPlan: async (request) => {
    const { data } = await getAdminApi().post(PLAN_BASE, request);
    return data;
  },
  updatePlan: async (id, request) => {
    const { data } = await getAdminApi().put(`${PLAN_BASE}/${id}`, request);
    return data;
  },
  deletePlan: async (id) => {
    await getAdminApi().delete(`${PLAN_BASE}/${id}`);
  },
  // Tenant Licences
  listTenantLicences: async (tenantId) => {
    const { data } = await getAdminApi().get(TENANT_BASE2, { params: { tenantId } });
    return data;
  },
  assignLicence: async (tenantId, request) => {
    const { data } = await getAdminApi().post(`${TENANT_BASE2}/${tenantId}`, request);
    return data;
  },
  revokeLicence: async (tenantId, licenceId) => {
    await getAdminApi().delete(`${TENANT_BASE2}/${tenantId}/${licenceId}`);
  }
};

// src/api/keycloakService.ts
var BASE6 = "/api/v1/admin/keycloak";
var keycloakService = {
  // Role Mappings
  getAvailableRoles: async (realm) => {
    const { data } = await getAdminApi().get(`${BASE6}/mappings/roles`, { params: { realm } });
    return data;
  },
  listMappings: async () => {
    const { data } = await getAdminApi().get(`${BASE6}/mappings`);
    return data;
  },
  getMapping: async (role) => {
    const { data } = await getAdminApi().get(`${BASE6}/mappings/${role}`);
    return data;
  },
  createMapping: async (request) => {
    const { data } = await getAdminApi().post(`${BASE6}/mappings`, request);
    return data;
  },
  updateMapping: async (role, request) => {
    const { data } = await getAdminApi().put(`${BASE6}/mappings/${role}`, request);
    return data;
  },
  deleteMapping: async (role) => {
    const { data } = await getAdminApi().delete(`${BASE6}/mappings/${role}`);
    return data;
  },
  countMappings: async () => {
    const { data } = await getAdminApi().get(`${BASE6}/mappings/count`);
    return data;
  },
  reloadMappings: async () => {
    const { data } = await getAdminApi().post(`${BASE6}/mappings/reload`);
    return data;
  },
  getMappingsMap: async () => {
    const { data } = await getAdminApi().get(`${BASE6}/mappings/map`);
    return data;
  },
  bulkCreateMappings: async (request) => {
    const { data } = await getAdminApi().post(`${BASE6}/mappings/bulk`, request);
    return data;
  },
  // Sync
  getSyncStatus: async () => {
    const { data } = await getAdminApi().get(`${BASE6}/sync/status`);
    return data;
  },
  getSyncHistory: async (limit = 20) => {
    const { data } = await getAdminApi().get(`${BASE6}/sync/history`, { params: { limit } });
    return data;
  },
  syncAllTenants: async () => {
    const { data } = await getAdminApi().post(`${BASE6}/sync/all`);
    return data;
  },
  syncTenant: async (tenantId, realm) => {
    const { data } = await getAdminApi().post(`${BASE6}/sync/tenant/${tenantId}`, null, { params: { realm } });
    return data;
  },
  toggleSync: async (enabled) => {
    const { data } = await getAdminApi().post(`${BASE6}/sync/toggle`, { enabled });
    return data;
  }
};

// src/hooks/useUsers.ts
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
var userKeys = {
  all: ["users"],
  lists: () => [...userKeys.all, "list"],
  list: (filters) => [...userKeys.lists(), filters],
  details: () => [...userKeys.all, "detail"],
  detail: (id) => [...userKeys.details(), id],
  current: () => [...userKeys.all, "current"],
  profiles: (id) => [...userKeys.all, "profiles", id]
};
function useUsers(filters) {
  return useQuery({
    queryKey: userKeys.list(filters || {}),
    queryFn: () => userService.listUsers(filters)
  });
}
function useUser(userId) {
  return useQuery({
    queryKey: userKeys.detail(userId),
    queryFn: () => userService.getUser(userId),
    enabled: !!userId
  });
}
function useCurrentUser() {
  return useQuery({
    queryKey: userKeys.current(),
    queryFn: () => userService.getCurrentUser(),
    staleTime: 5 * 60 * 1e3
  });
}
function useUserProfileIds(userId) {
  return useQuery({
    queryKey: userKeys.profiles(userId),
    queryFn: () => userService.getUserProfileIds(userId),
    enabled: !!userId
  });
}
function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => userService.createUser(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.lists() });
    }
  });
}
function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }) => userService.updateUser(userId, data),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: userKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: userKeys.lists() });
    }
  });
}
function useChangeUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, status }) => userService.changeUserStatus(userId, status),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: userKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: userKeys.lists() });
    }
  });
}
function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId) => userService.deleteUser(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.lists() });
    }
  });
}
function useAssignProfiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, profileIds }) => userService.assignProfiles(userId, profileIds),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: userKeys.profiles(userId) });
    }
  });
}

// src/hooks/useTenants.ts
import { useQuery as useQuery2, useMutation as useMutation2, useQueryClient as useQueryClient2 } from "@tanstack/react-query";
var tenantKeys = {
  all: ["tenants"],
  lists: () => [...tenantKeys.all, "list"],
  details: () => [...tenantKeys.all, "detail"],
  detail: (id) => [...tenantKeys.details(), id]
};
function useTenants() {
  return useQuery2({
    queryKey: tenantKeys.lists(),
    queryFn: () => tenantService.listTenants()
  });
}
function useTenant(tenantId) {
  return useQuery2({
    queryKey: tenantKeys.detail(tenantId),
    queryFn: () => tenantService.getTenant(tenantId),
    enabled: !!tenantId
  });
}
function useCreateTenant() {
  const qc = useQueryClient2();
  return useMutation2({
    mutationFn: (data) => tenantService.createTenant(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKeys.lists() });
    }
  });
}
function useUpdateTenant() {
  const qc = useQueryClient2();
  return useMutation2({
    mutationFn: ({ tenantId, data }) => tenantService.updateTenant(tenantId, data),
    onSuccess: (_, { tenantId }) => {
      qc.invalidateQueries({ queryKey: tenantKeys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: tenantKeys.lists() });
    }
  });
}
function useChangeTenantStatus() {
  const qc = useQueryClient2();
  return useMutation2({
    mutationFn: ({ tenantId, status }) => tenantService.changeTenantStatus(tenantId, status),
    onSuccess: (_, { tenantId }) => {
      qc.invalidateQueries({ queryKey: tenantKeys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: tenantKeys.lists() });
    }
  });
}
function useChangeTenantType() {
  const qc = useQueryClient2();
  return useMutation2({
    mutationFn: ({ tenantId, type }) => tenantService.changeTenantType(tenantId, type),
    onSuccess: (_, { tenantId }) => {
      qc.invalidateQueries({ queryKey: tenantKeys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: tenantKeys.lists() });
    }
  });
}
function useExtendTrial() {
  const qc = useQueryClient2();
  return useMutation2({
    mutationFn: ({ tenantId, days }) => tenantService.extendTrial(tenantId, days),
    onSuccess: (_, { tenantId }) => {
      qc.invalidateQueries({ queryKey: tenantKeys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: tenantKeys.lists() });
    }
  });
}
function useDeleteTenant() {
  const qc = useQueryClient2();
  return useMutation2({
    mutationFn: (tenantId) => tenantService.deleteTenant(tenantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKeys.lists() });
    }
  });
}

// src/hooks/usePermissions.ts
import { useQuery as useQuery3, useMutation as useMutation3, useQueryClient as useQueryClient3 } from "@tanstack/react-query";
var permissionKeys = {
  all: ["permissions"],
  lists: () => [...permissionKeys.all, "list"],
  list: (filters) => [...permissionKeys.lists(), filters],
  details: () => [...permissionKeys.all, "detail"],
  detail: (id) => [...permissionKeys.details(), id],
  byUser: (userId) => [...permissionKeys.all, "user", userId],
  byModule: (module) => [...permissionKeys.all, "module", module]
};
function usePermissions(filters) {
  return useQuery3({
    queryKey: permissionKeys.list(filters || {}),
    queryFn: () => permissionService.listPermissions(filters)
  });
}
function usePermission(permissionId) {
  return useQuery3({
    queryKey: permissionKeys.detail(permissionId),
    queryFn: () => permissionService.getPermission(permissionId),
    enabled: !!permissionId && permissionId > 0
  });
}
function useUserPermissions(userId) {
  return useQuery3({
    queryKey: permissionKeys.byUser(userId),
    queryFn: () => permissionService.getUserPermissions(userId),
    enabled: !!userId
  });
}
function useModulePermissions(module) {
  return useQuery3({
    queryKey: permissionKeys.byModule(module),
    queryFn: () => permissionService.getModulePermissions(module),
    enabled: !!module
  });
}
function useActivePermissions() {
  return useQuery3({
    queryKey: permissionKeys.list({ isActive: true }),
    queryFn: () => permissionService.getActivePermissions()
  });
}
function useCreatePermission() {
  const qc = useQueryClient3();
  return useMutation3({
    mutationFn: (data) => permissionService.createPermission(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: permissionKeys.lists() });
    }
  });
}
function useUpdatePermission() {
  const qc = useQueryClient3();
  return useMutation3({
    mutationFn: ({ id, data }) => permissionService.updatePermission(id, data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: permissionKeys.detail(updated.id) });
      qc.invalidateQueries({ queryKey: permissionKeys.lists() });
      if (updated.userId) qc.invalidateQueries({ queryKey: permissionKeys.byUser(updated.userId) });
      if (updated.module) qc.invalidateQueries({ queryKey: permissionKeys.byModule(updated.module) });
    }
  });
}
function useDeletePermission() {
  const qc = useQueryClient3();
  return useMutation3({
    mutationFn: (permissionId) => permissionService.deletePermission(permissionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: permissionKeys.all });
    }
  });
}
function useTogglePermission() {
  const qc = useQueryClient3();
  return useMutation3({
    mutationFn: ({ id, isActive, version }) => permissionService.updatePermission(id, { isActive, version }),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: permissionKeys.detail(updated.id) });
      qc.invalidateQueries({ queryKey: permissionKeys.lists() });
    }
  });
}

// src/hooks/useProfiles.ts
import { useQuery as useQuery4, useMutation as useMutation4, useQueryClient as useQueryClient4 } from "@tanstack/react-query";
var profileKeys = {
  all: ["profiles"],
  lists: () => [...profileKeys.all, "list"],
  list: (filters) => [...profileKeys.lists(), filters],
  details: () => [...profileKeys.all, "detail"],
  detail: (id) => [...profileKeys.details(), id]
};
function useProfiles(filters) {
  return useQuery4({
    queryKey: profileKeys.list(filters || {}),
    queryFn: () => profileService.listProfiles(filters),
    staleTime: 5 * 60 * 1e3
  });
}
function useProfile(id) {
  return useQuery4({
    queryKey: profileKeys.detail(id),
    queryFn: () => profileService.getProfile(id),
    enabled: !!id
  });
}
function useCreateProfile() {
  const qc = useQueryClient4();
  return useMutation4({
    mutationFn: (data) => profileService.createProfile(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: profileKeys.lists() });
    }
  });
}
function useUpdateProfile() {
  const qc = useQueryClient4();
  return useMutation4({
    mutationFn: ({ id, data }) => profileService.updateProfile(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: profileKeys.detail(id) });
      qc.invalidateQueries({ queryKey: profileKeys.lists() });
    }
  });
}
function useDeleteProfile() {
  const qc = useQueryClient4();
  return useMutation4({
    mutationFn: (id) => profileService.deleteProfile(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: profileKeys.lists() });
    }
  });
}
function useAddProfilePermission() {
  const qc = useQueryClient4();
  return useMutation4({
    mutationFn: ({ profileId, permissionId }) => profileService.addPermission(profileId, permissionId),
    onSuccess: (_, { profileId }) => {
      qc.invalidateQueries({ queryKey: profileKeys.detail(profileId) });
    }
  });
}
function useRemoveProfilePermission() {
  const qc = useQueryClient4();
  return useMutation4({
    mutationFn: ({ profileId, permissionId }) => profileService.removePermission(profileId, permissionId),
    onSuccess: (_, { profileId }) => {
      qc.invalidateQueries({ queryKey: profileKeys.detail(profileId) });
    }
  });
}

// src/hooks/useModules.ts
import { useQuery as useQuery5, useMutation as useMutation5, useQueryClient as useQueryClient5 } from "@tanstack/react-query";
var moduleKeys = {
  all: ["modules"],
  lists: () => [...moduleKeys.all, "list"],
  details: () => [...moduleKeys.all, "detail"],
  detail: (id) => [...moduleKeys.details(), id],
  tenantModules: (tenantId) => [...moduleKeys.all, "tenant", tenantId]
};
function useModules() {
  return useQuery5({
    queryKey: moduleKeys.lists(),
    queryFn: () => moduleService.listModules()
  });
}
function useModule(id) {
  return useQuery5({
    queryKey: moduleKeys.detail(id),
    queryFn: () => moduleService.getModule(id),
    enabled: !!id
  });
}
function useTenantModules(tenantId) {
  return useQuery5({
    queryKey: moduleKeys.tenantModules(tenantId),
    queryFn: () => moduleService.listTenantModules(tenantId),
    enabled: !!tenantId
  });
}
function useCreateModule() {
  const qc = useQueryClient5();
  return useMutation5({
    mutationFn: (data) => moduleService.createModule(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: moduleKeys.lists() });
    }
  });
}
function useUpdateModule() {
  const qc = useQueryClient5();
  return useMutation5({
    mutationFn: ({ id, data }) => moduleService.updateModule(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: moduleKeys.detail(id) });
      qc.invalidateQueries({ queryKey: moduleKeys.lists() });
    }
  });
}
function useDeleteModule() {
  const qc = useQueryClient5();
  return useMutation5({
    mutationFn: (id) => moduleService.deleteModule(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: moduleKeys.lists() });
    }
  });
}
function useActivateModule() {
  const qc = useQueryClient5();
  return useMutation5({
    mutationFn: ({ tenantId, moduleId }) => moduleService.activateModule(tenantId, moduleId),
    onSuccess: (_, { tenantId }) => {
      qc.invalidateQueries({ queryKey: moduleKeys.tenantModules(tenantId) });
    }
  });
}
function useDeactivateModule() {
  const qc = useQueryClient5();
  return useMutation5({
    mutationFn: ({ tenantId, moduleId }) => moduleService.deactivateModule(tenantId, moduleId),
    onSuccess: (_, { tenantId }) => {
      qc.invalidateQueries({ queryKey: moduleKeys.tenantModules(tenantId) });
    }
  });
}

// src/hooks/useLicences.ts
import { useQuery as useQuery6, useMutation as useMutation6, useQueryClient as useQueryClient6 } from "@tanstack/react-query";
var licenceKeys = {
  all: ["licences"],
  plans: () => [...licenceKeys.all, "plans"],
  plan: (id) => [...licenceKeys.plans(), id],
  tenantLicences: (tenantId) => [...licenceKeys.all, "tenant", tenantId]
};
function useLicencePlans() {
  return useQuery6({
    queryKey: licenceKeys.plans(),
    queryFn: () => licenceService.listPlans()
  });
}
function useLicencePlan(id) {
  return useQuery6({
    queryKey: licenceKeys.plan(id),
    queryFn: () => licenceService.getPlan(id),
    enabled: !!id
  });
}
function useTenantLicences(tenantId) {
  return useQuery6({
    queryKey: licenceKeys.tenantLicences(tenantId),
    queryFn: () => licenceService.listTenantLicences(tenantId),
    enabled: !!tenantId
  });
}
function useCreateLicencePlan() {
  const qc = useQueryClient6();
  return useMutation6({
    mutationFn: (data) => licenceService.createPlan(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: licenceKeys.plans() });
    }
  });
}
function useUpdateLicencePlan() {
  const qc = useQueryClient6();
  return useMutation6({
    mutationFn: ({ id, data }) => licenceService.updatePlan(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: licenceKeys.plan(id) });
      qc.invalidateQueries({ queryKey: licenceKeys.plans() });
    }
  });
}
function useDeleteLicencePlan() {
  const qc = useQueryClient6();
  return useMutation6({
    mutationFn: (id) => licenceService.deletePlan(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: licenceKeys.plans() });
    }
  });
}
function useAssignLicence() {
  const qc = useQueryClient6();
  return useMutation6({
    mutationFn: ({ tenantId, data }) => licenceService.assignLicence(tenantId, data),
    onSuccess: (_, { tenantId }) => {
      qc.invalidateQueries({ queryKey: licenceKeys.tenantLicences(tenantId) });
    }
  });
}
function useRevokeLicence() {
  const qc = useQueryClient6();
  return useMutation6({
    mutationFn: ({ tenantId, licenceId }) => licenceService.revokeLicence(tenantId, licenceId),
    onSuccess: (_, { tenantId }) => {
      qc.invalidateQueries({ queryKey: licenceKeys.tenantLicences(tenantId) });
    }
  });
}

// src/hooks/useKeycloakSync.ts
import { useQuery as useQuery7, useMutation as useMutation7, useQueryClient as useQueryClient7 } from "@tanstack/react-query";
var keycloakSyncKeys = {
  all: ["keycloak-sync"],
  status: () => [...keycloakSyncKeys.all, "status"],
  history: () => [...keycloakSyncKeys.all, "history"],
  historyWithLimit: (limit) => [...keycloakSyncKeys.history(), { limit }],
  mappings: () => [...keycloakSyncKeys.all, "mappings"],
  roles: (realm) => [...keycloakSyncKeys.all, "roles", realm]
};
function useSyncStatus(options) {
  return useQuery7({
    queryKey: keycloakSyncKeys.status(),
    queryFn: () => keycloakService.getSyncStatus(),
    refetchInterval: 1e4,
    staleTime: 5e3,
    ...options
  });
}
function useSyncHistory(limit = 20, options) {
  return useQuery7({
    queryKey: keycloakSyncKeys.historyWithLimit(limit),
    queryFn: () => keycloakService.getSyncHistory(limit),
    refetchInterval: 1e4,
    staleTime: 5e3,
    ...options
  });
}
function useSyncAllTenants() {
  const qc = useQueryClient7();
  return useMutation7({
    mutationFn: () => keycloakService.syncAllTenants(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keycloakSyncKeys.status() });
      qc.invalidateQueries({ queryKey: keycloakSyncKeys.history() });
    }
  });
}
function useSyncTenant() {
  const qc = useQueryClient7();
  return useMutation7({
    mutationFn: ({ tenantId, realm }) => keycloakService.syncTenant(tenantId, realm),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keycloakSyncKeys.status() });
      qc.invalidateQueries({ queryKey: keycloakSyncKeys.history() });
    }
  });
}
function useToggleSync() {
  const qc = useQueryClient7();
  return useMutation7({
    mutationFn: (enabled) => keycloakService.toggleSync(enabled),
    onMutate: async (enabled) => {
      await qc.cancelQueries({ queryKey: keycloakSyncKeys.status() });
      const previous = qc.getQueryData(keycloakSyncKeys.status());
      if (previous) {
        qc.setQueryData(keycloakSyncKeys.status(), { ...previous, enabled });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(keycloakSyncKeys.status(), context.previous);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keycloakSyncKeys.status() });
    }
  });
}
function useAvailableRoles(realm) {
  return useQuery7({
    queryKey: keycloakSyncKeys.roles(realm),
    queryFn: () => keycloakService.getAvailableRoles(realm)
  });
}
function useRoleMappings() {
  return useQuery7({
    queryKey: keycloakSyncKeys.mappings(),
    queryFn: () => keycloakService.listMappings()
  });
}
function useCreateRoleMapping() {
  const qc = useQueryClient7();
  return useMutation7({
    mutationFn: (data) => keycloakService.createMapping(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keycloakSyncKeys.mappings() });
    }
  });
}
function useUpdateRoleMapping() {
  const qc = useQueryClient7();
  return useMutation7({
    mutationFn: ({ role, data }) => keycloakService.updateMapping(role, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keycloakSyncKeys.mappings() });
    }
  });
}
function useDeleteRoleMapping() {
  const qc = useQueryClient7();
  return useMutation7({
    mutationFn: (role) => keycloakService.deleteMapping(role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keycloakSyncKeys.mappings() });
    }
  });
}
function useBulkCreateMappings() {
  const qc = useQueryClient7();
  return useMutation7({
    mutationFn: (data) => keycloakService.bulkCreateMappings(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keycloakSyncKeys.mappings() });
    }
  });
}
function useReloadMappings() {
  const qc = useQueryClient7();
  return useMutation7({
    mutationFn: () => keycloakService.reloadMappings(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keycloakSyncKeys.mappings() });
    }
  });
}

// src/stores/useAdminStore.ts
import { create } from "zustand";
var useAdminStore = create((set, get) => ({
  currentUser: null,
  setCurrentUser: (user) => set({ currentUser: user }),
  hasPermission: ({ module, feature }) => {
    const user = get().currentUser;
    if (!user?.permissions?.actions) return false;
    return user.permissions.actions.some(
      (p) => p.module === module && p.feature === feature && p.hasAccess
    );
  },
  hasAnyPermission: (checks) => {
    return checks.some((c) => get().hasPermission(c));
  },
  getAdminContext: () => {
    return get().currentUser?.adminContext ?? null;
  }
}));

// src/guards/usePermissionCheck.ts
function usePermissionCheck(check) {
  const { data: user } = useCurrentUser();
  const hasPermission = (module, feature) => {
    const currentUser = user;
    if (!currentUser?.permissions?.actions) return false;
    return currentUser.permissions.actions.some(
      (p) => p.module === module && p.feature === feature && p.hasAccess
    );
  };
  if (Array.isArray(check)) {
    return check.some((c) => hasPermission(c.module, c.feature));
  }
  return hasPermission(check.module, check.feature);
}
function useAdminContext() {
  const { data: user } = useCurrentUser();
  return user?.adminContext;
}
function useIsAdmin() {
  return useAdminContext()?.isAdmin ?? false;
}
function useCanManageUsers() {
  return useAdminContext()?.canManageUsers ?? false;
}
function useCanManageTenants() {
  return useAdminContext()?.canManageTenants ?? false;
}
function useCanManagePermissions() {
  return useAdminContext()?.canManagePermissions ?? false;
}
function useCanSyncKeycloak() {
  return useAdminContext()?.canSyncKeycloak ?? false;
}

// src/guards/PermissionGuard.tsx
import { Fragment, jsx } from "react/jsx-runtime";
function PermissionGuard({ children, require: require2, fallback = null }) {
  const hasPermission = usePermissionCheck(require2);
  if (!hasPermission) {
    return /* @__PURE__ */ jsx(Fragment, { children: fallback });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}

// src/validation/schemas.ts
import { z } from "zod";
var createUserSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters").max(50),
  email: z.string().email("Invalid email address"),
  firstName: z.string().min(1, "First name is required").max(100),
  lastName: z.string().min(1, "Last name is required").max(100),
  password: z.string().min(8, "Password must be at least 8 characters").optional(),
  tenantId: z.string().min(1, "Tenant is required"),
  keycloakUserId: z.string().optional()
});
var updateUserSchema = z.object({
  firstName: z.string().min(1).max(100).optional(),
  lastName: z.string().min(1).max(100).optional(),
  email: z.string().email().optional(),
  status: z.enum(["ACTIVE", "INACTIVE", "SUSPENDED", "PENDING"]).optional(),
  version: z.number().optional()
});
var createTenantSchema = z.object({
  tenantId: z.string().min(2, "Tenant ID must be at least 2 characters").max(50).regex(/^[a-z0-9_-]+$/, "Only lowercase letters, numbers, hyphens, and underscores"),
  name: z.string().min(2, "Name must be at least 2 characters").max(100),
  description: z.string().max(500).optional(),
  type: z.enum(["CENTRAL_AUTHORITY", "MEMBER_SELF_MANAGED", "MEMBER_HOSTED", "MEMBER_HOSTED_LIGHT"]),
  domain: z.string().url().optional().or(z.literal(""))
});
var updateTenantSchema = z.object({
  name: z.string().min(2).max(100).optional(),
  description: z.string().max(500).optional(),
  domain: z.string().url().optional().or(z.literal("")),
  version: z.number().optional()
});
var createProfileSchema = z.object({
  name: z.string().min(2, "Profile name must be at least 2 characters").max(100),
  description: z.string().max(500).optional(),
  permissionIds: z.array(z.number()).optional()
});
var createPermissionSchema = z.object({
  module: z.string().min(1, "Module is required"),
  feature: z.string().min(1, "Feature is required"),
  level: z.enum(["VIEW", "CREATE", "EDIT", "DELETE", "MANAGE", "ADMIN"]),
  scopeType: z.enum(["GLOBAL", "TENANT", "USER"]),
  constraintType: z.enum(["LEVEL", "AVAILABILITY", "STRING_ARRAY", "SCOPE"]),
  constraintValue: z.string().optional(),
  description: z.string().max(500).optional(),
  profileId: z.number().optional()
});
var roleMappingSchema = z.object({
  keycloak_role: z.string().min(1, "Keycloak role is required"),
  profile_id: z.number().min(1, "Profile is required"),
  description: z.string().max(500).optional(),
  active: z.boolean().optional()
});
export {
  CONSTRAINT_TYPE_LABELS,
  MODULES,
  PERMISSION_LEVELS,
  PermissionGuard,
  SCOPE_TYPE_LABELS,
  TENANT_STATUS_LABELS,
  TENANT_TYPE_LABELS,
  createPermissionSchema,
  createProfileSchema,
  createTenantSchema,
  createUserSchema,
  getAdminApi,
  initAdminApi,
  keycloakService,
  keycloakSyncKeys,
  licenceKeys,
  licenceService,
  moduleKeys,
  moduleService,
  permissionKeys,
  permissionService,
  profileKeys,
  profileService,
  roleMappingSchema,
  tenantKeys,
  tenantService,
  updateTenantSchema,
  updateUserSchema,
  useActivateModule,
  useActivePermissions,
  useAddProfilePermission,
  useAdminContext,
  useAdminStore,
  useAssignLicence,
  useAssignProfiles,
  useAvailableRoles,
  useBulkCreateMappings,
  useCanManagePermissions,
  useCanManageTenants,
  useCanManageUsers,
  useCanSyncKeycloak,
  useChangeTenantStatus,
  useChangeTenantType,
  useChangeUserStatus,
  useCreateLicencePlan,
  useCreateModule,
  useCreatePermission,
  useCreateProfile,
  useCreateRoleMapping,
  useCreateTenant,
  useCreateUser,
  useCurrentUser,
  useDeactivateModule,
  useDeleteLicencePlan,
  useDeleteModule,
  useDeletePermission,
  useDeleteProfile,
  useDeleteRoleMapping,
  useDeleteTenant,
  useDeleteUser,
  useExtendTrial,
  useIsAdmin,
  useLicencePlan,
  useLicencePlans,
  useModule,
  useModulePermissions,
  useModules,
  usePermission,
  usePermissionCheck,
  usePermissions,
  useProfile,
  useProfiles,
  useReloadMappings,
  useRemoveProfilePermission,
  useRevokeLicence,
  useRoleMappings,
  useSyncAllTenants,
  useSyncHistory,
  useSyncStatus,
  useSyncTenant,
  useTenant,
  useTenantLicences,
  useTenantModules,
  useTenants,
  useTogglePermission,
  useToggleSync,
  useUpdateLicencePlan,
  useUpdateModule,
  useUpdatePermission,
  useUpdateProfile,
  useUpdateRoleMapping,
  useUpdateTenant,
  useUpdateUser,
  useUser,
  useUserPermissions,
  useUserProfileIds,
  useUsers,
  userKeys,
  userService
};
//# sourceMappingURL=index.mjs.map